import { useState, useEffect } from 'react';
import { useMessages, useConfig } from '@ynap/immutables-provider';

export const useContactUs = ({ selectedProduct, setUser, isSizeAndFit = false, isReservation = false, productLink }) => {
    const [contactDetailsMessage, setContactDetailsMessage] = useState({});
    const personalShopperImg = useConfig((config) => config?.head?.pdp?.personalShopperImg);
    const sizeAndFitMessages = useMessages((messages) => messages?.contactUsCTA?.sizeAndFit);
    const reservationsMessages = useMessages((messages) => messages?.reservations?.contactUsCTA);
    const platinumMessages = useMessages((messages) => messages?.contactUsCTA?.platinum);
    const purchaseMessages = useMessages((messages) => messages?.contactUsCTA?.generalPurchase);
    const fwjMessages = useMessages((messages) => messages?.contactUsCTA?.fineWatchesJewellery);

    useEffect(() => {
        const setContactDetails = async () => {
            try {
                const response = await getUserSummaryResponse(setUser);
                const { oneSize, isWatchExperts: isWatchExpertsProduct, isMRPJewelry, type, canAddToBag } = selectedProduct;
                const isPlatinum = response?.loyaltySegment === 'eipplatinum';
                const filteredMessages = filterMessages(selectedProduct, response?.isEip, isPlatinum);
                const isWatchExperts = isWatchExpertsProduct && (isSizeAndFit ? sizeAndFitMessages?.watchExperts : filteredMessages?.watchExperts);

                const { getContactDetails = () => {} } = window?.SF || {};
                const { imageURL } = response?.personalShopperDetails || {};
                const psImageURL = personalShopperImg && imageURL ? imageURL.replace('{{width}}', 200) : null;
                const {
                    personalShopper = {},
                    customerCare = {},
                    watchExpert = {},
                    highPricingProductContact = '',
                    freeContactNumber = '',
                } = getContactDetails() || {};

                const { name } = personalShopper;

                const isHighPPItem = type === 'highPPItem';
                const bodyUrl = productLink ? `${window?.location.origin}${productLink}` : window?.location.href;

                const { email, telephone } = getTelephoneAndEmail(
                    customerCare,
                    personalShopper,
                    response?.isEip,
                    isHighPPItem,
                    isPlatinum,
                    isWatchExpertsProduct && watchExpert.email ? watchExpert : undefined,
                    isMRPJewelry,
                    freeContactNumber
                );

                if (isWatchExpertsProduct || isMRPJewelry) {
                    setContactDetailsMessage({
                        isPlatinum,
                        isEip: response?.isEip,
                        personalShopperName: name,
                        personalShopperImg: psImageURL,
                        freeContactNumber,
                        title: getFWJTitle(fwjMessages, canAddToBag, name),
                        call: telephone || '',
                        emailLabel: sizeAndFitMessages.emailLabel(),
                        sendEmailStructure: getEmailStructure(
                            customerCare,
                            personalShopper,
                            filteredMessages,
                            selectedProduct,
                            response?.isEip,
                            isWatchExpertsProduct && watchExpert.email ? watchExpert : undefined,
                            highPricingProductContact,
                            bodyUrl,
                            isPlatinum,
                            purchaseMessages,
                            isMRPJewelry
                        ),
                    });
                } else if (!isSizeAndFit && filteredMessages) {
                    setContactDetailsMessage({
                        isPlatinum,
                        isHighPPItem,
                        isEip: response?.isEip,
                        call: telephone,
                        personalShopperName: name,
                        personalShopperImg: psImageURL,
                        emailLabel: filteredMessages.emailLabel(),
                        title: getFilterMessagesTitle(filteredMessages, name, oneSize, isWatchExperts, response?.isEip, isPlatinum, platinumMessages),
                        freeContactNumber: freeContactNumber,
                        sendEmailStructure: getEmailStructure(
                            customerCare,
                            personalShopper,
                            filteredMessages,
                            selectedProduct,
                            response?.isEip,
                            isWatchExpertsProduct && watchExpert.email ? watchExpert : undefined,
                            highPricingProductContact,
                            bodyUrl,
                            isPlatinum,
                        ),
                    });
                } else if (isSizeAndFit) {
                    setContactDetailsMessage({
                        isPlatinum,
                        isEip: response?.isEip,
                        call: telephone,
                        personalShopperName: name,
                        personalShopperImg: psImageURL,
                        emailLabel: sizeAndFitMessages.emailLabel(),
                        title: getSizeAndFitTitle(sizeAndFitMessages, response?.isEip, name, oneSize, isWatchExperts, isPlatinum, platinumMessages),
                        freeContactNumber: freeContactNumber,
                        sendEmailStructure: {
                            email,
                            subject: sizeAndFitMessages.emailStructure.subject({ name: selectedProduct.name }),
                            body: sizeAndFitMessages.emailStructure.body({ url: bodyUrl }),
                        },
                    });
                } else if (isReservation) {
                    setContactDetailsMessage({
                        isPlatinum,
                        call: telephone,
                        emailLabel: reservationsMessages.emailLabel(),
                        freeContactNumber: freeContactNumber,
                        sendEmailStructure: {
                            email,
                            subject: reservationsMessages.emailStructure.subject({ name: selectedProduct.name, size: selectedProduct.size }),
                            body: reservationsMessages.emailStructure.body({ name: selectedProduct.name, size: selectedProduct.size, url: bodyUrl }),
                        },
                    });
                } else {
                    setContactDetailsMessage({
                        isPlatinum,
                        isEip: response?.isEip,
                        freeContactNumber: freeContactNumber,
                        call: telephone || '',
                        sendEmailStructure: {
                            email,
                            subject: '',
                            body: '',
                        },
                    });
                }
            } catch (error) {
                return error;
            }
        };
        setContactDetails();
    }, [setContactDetailsMessage, productLink, selectedProduct.name]);
    return contactDetailsMessage;
};

const getTelephoneAndEmail = (customerCare, personalShopper, isEip, isHighPPItem, isPlatinum, watchExpert, isMRPJewelry, freeContactNumber) => {
    // personal shopper details (even without a personal shopper assigned take precendence) default telephone to customer care though.
    const customerCareTelephone = customerCare && customerCare.telephone ? customerCare.telephone : '';
    const customerCareEmail = customerCare && customerCare.email ? customerCare.email : '';
    const { telephone: personalShopperTelephone = '', email: personalShopperEmail, name } = personalShopper;
    const { email: watchExpertEmail } = watchExpert || {};

    if ((isEip || isPlatinum) && name) return { telephone: personalShopperTelephone, email: personalShopperEmail };
    if (isEip && watchExpert) return { telephone: personalShopperTelephone, email: watchExpertEmail };
    if (isPlatinum) return { telephone: personalShopperTelephone, email: personalShopperEmail };
    if (isMRPJewelry) return { telephone: customerCareTelephone, email: customerCareEmail };
    if (watchExpert) return { telephone: freeContactNumber, email: watchExpertEmail };

    return {
        telephone: isEip ? personalShopperTelephone : !isHighPPItem ? customerCareTelephone : '',
        email: personalShopperEmail ? personalShopperEmail : customerCareEmail,
    };
};

const getSizeAndFitTitle = (sizeAndFitMessages, isEip, name, oneSize, isWatchExperts, isPlatinum, platinumMessages) => {
    if (isEip && name) return sizeAndFitMessages.titlePersonalShopper({ oneSize, name });
    if (isPlatinum) return getPlatinumTitle(platinumMessages, isPlatinum, name);
    if (isWatchExperts) return sizeAndFitMessages.watchExperts();
    if (isEip) return sizeAndFitMessages.titleEip({ oneSize });
    return sizeAndFitMessages.title({ oneSize });
};

const getFilterMessagesTitle = (filteredMessages, name, oneSize, isWatchExperts, isEip, isPlatinum, platinumMessages) => {
    if (name) return filteredMessages.titleAssignedPS({ name, oneSize });
    if (isWatchExperts) return filteredMessages.watchExperts();
    if (isPlatinum) return getPlatinumTitle(platinumMessages, isPlatinum, name);
    if (!isEip && filteredMessages?.titleNonEip) return filteredMessages.titleNonEip();
    return filteredMessages.title({ oneSize });
};

const getFWJTitle = (fwjMessages, isBuyable, name) => {
  if (!name) {
    return fwjMessages.withoutPS.title();
  }
  return isBuyable
    ? fwjMessages.withPS.inStockTitle({ name })
    : fwjMessages.withPS.soldOutTitle({ name });
};

const getPlatinumTitle = (platinumMessages, isPlatinum, name) => {
    if (isPlatinum && name) return platinumMessages.titleAssignedPS({ name });
    if (isPlatinum) return platinumMessages.title();
    return '';
};

const getUserSummaryResponse = async (setUser) => {
    const { getUserSummary = () => {} } = window?.SF || {};
    const user = await getUserSummary();
    user && setUser && setUser(user);
    return getUserSummary();
};

const getEmailStructure = (
    customerCare = {},
    personalShopper = {},
    filteredMessages = {},
    selectedProduct,
    isEip,
    watchExpert,
    highPricingProductContact,
    bodyUrl,
    isPlatinum,
    purchaseMessages,
    isMRPJewelry = false
) => {
    const emailStructure = filteredMessages?.emailStructure;
    const productName = selectedProduct?.name;
    let email = highPricingProductContact !== '' ? highPricingProductContact : customerCare.email;
    const subject = emailStructure ? emailStructure.subject({ name: productName }) : purchaseMessages?.emailStructure?.subject({ name: productName });
    const body = emailStructure ? emailStructure.body({ url: bodyUrl, email }) : purchaseMessages?.emailStructure.body({ url: bodyUrl });
    
    if ((isEip || isPlatinum) && personalShopper.name) {
        email = personalShopper.email;
    } else if (watchExpert) {
        email = watchExpert.email;
    } else if (isMRPJewelry) {
        email = customerCare.email;
    } else if (isEip || isPlatinum) {
        email = personalShopper.email ? personalShopper.email : '';
    }

    return {
        email,
        subject: productName ? subject : '',
        body: productName ? body : ''
    };
};

const filterMessages = (selectedProduct, isEip, isPlatinum) => {
    const { personalShopperNotification, eipPersonalShopperNotification } = selectedProduct || {};
    if (!personalShopperNotification && !eipPersonalShopperNotification) return;

    if ((isEip || isPlatinum) && eipPersonalShopperNotification) {
        return eipPersonalShopperNotification;
    } else if (personalShopperNotification) {
        return personalShopperNotification;
    }
};
